import React, { useCallback, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { ClientOption, FilterOptions, ResponsibilityOption } from '../hooks/useFilterOptions';
import { useAnchoredPopover } from '../hooks/useAnchoredPopover';

export interface FilterState {
    clientIds: number[];
    cities: string[];
    responsibilityIds: number[];
}

interface Props {
    options: FilterOptions | null;
    value: FilterState;
    onChange: (next: FilterState) => void;
}

type Dimension = 'client' | 'city' | 'responsibility';

const FilterBar: React.FC<Props> = ({ options, value, onChange }) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string) =>
        intl.formatMessage({ id, defaultMessage });

    const [pickerOpen, setPickerOpen] = useState(false);
    const [activeDim, setActiveDim] = useState<Dimension | null>(null);
    const addBtnRef = useRef<HTMLButtonElement>(null);

    const closePicker = useCallback(() => {
        setPickerOpen(false);
        setActiveDim(null);
    }, []);

    // Popovers portal to the .av2-page root (escape the sticky toolbar's
    // stacking context, keep the --av2-* token scope); the hook owns
    // positioning + click-outside + Escape. Only one popover renders at a
    // time, so the single panelRef always tracks it.
    const { panelRef, style: popStyle, portalTarget } = useAnchoredPopover(addBtnRef, pickerOpen, closePicker, 'start');

    const removeClient = (id: number) =>
        onChange({ ...value, clientIds: value.clientIds.filter((x) => x !== id) });
    const removeCity = (city: string) =>
        onChange({ ...value, cities: value.cities.filter((x) => x !== city) });
    const removeResp = (id: number) =>
        onChange({ ...value, responsibilityIds: value.responsibilityIds.filter((x) => x !== id) });

    const clearAll = () =>
        onChange({ clientIds: [], cities: [], responsibilityIds: [] });

    const hasAny =
        value.clientIds.length + value.cities.length + value.responsibilityIds.length > 0;

    const clientName = (id: number): string =>
        options?.clients.find((c) => c.id === id)?.name ?? `#${id}`;
    const respName = (id: number): string =>
        options?.responsibilities.find((r) => r.id === id)?.name ?? `#${id}`;

    return (
        <div className="av2-filters">
            <span className="av2-controls__label">
                <FormattedMessage id="analyticsV2.filter.label" defaultMessage="Filters" />
            </span>

            <div className="av2-filters__chips">
                {value.clientIds.map((id) => (
                    <span key={`c-${id}`} className="av2-chip">
                        <span className="av2-chip__dim">
                            <FormattedMessage id="analyticsV2.filter.clientLabel" defaultMessage="Client" />:
                        </span>
                        <span className="av2-chip__value">{clientName(id)}</span>
                        <button
                            type="button"
                            className="av2-chip__remove"
                            onClick={() => removeClient(id)}
                            aria-label={fm('analyticsV2.filter.remove', 'Remove')}
                        >
                            ×
                        </button>
                    </span>
                ))}
                {value.cities.map((city) => (
                    <span key={`y-${city}`} className="av2-chip">
                        <span className="av2-chip__dim">
                            <FormattedMessage id="analyticsV2.filter.cityLabel" defaultMessage="City" />:
                        </span>
                        <span className="av2-chip__value">{city}</span>
                        <button
                            type="button"
                            className="av2-chip__remove"
                            onClick={() => removeCity(city)}
                            aria-label={fm('analyticsV2.filter.remove', 'Remove')}
                        >
                            ×
                        </button>
                    </span>
                ))}
                {value.responsibilityIds.map((id) => (
                    <span key={`r-${id}`} className="av2-chip">
                        <span className="av2-chip__dim">
                            <FormattedMessage
                                id="analyticsV2.filter.responsibilityLabel"
                                defaultMessage="Stage"
                            />:
                        </span>
                        <span className="av2-chip__value">{respName(id)}</span>
                        <button
                            type="button"
                            className="av2-chip__remove"
                            onClick={() => removeResp(id)}
                            aria-label={fm('analyticsV2.filter.remove', 'Remove')}
                        >
                            ×
                        </button>
                    </span>
                ))}

                <div className="av2-filters__add">
                    <button
                        ref={addBtnRef}
                        type="button"
                        className="av2-filters__add-btn"
                        onClick={() => {
                            setPickerOpen((v) => !v);
                            setActiveDim(null);
                        }}
                    >
                        <span aria-hidden="true">+</span>
                        <FormattedMessage id="analyticsV2.filter.add" defaultMessage="Add filter" />
                    </button>

                    {pickerOpen && !activeDim && portalTarget && ReactDOM.createPortal(
                        <div className="av2-pop" ref={panelRef} style={popStyle}>
                            <button
                                type="button"
                                className="av2-pop__option"
                                onClick={() => setActiveDim('client')}
                            >
                                <FormattedMessage
                                    id="analyticsV2.filter.client"
                                    defaultMessage="Client"
                                />
                            </button>
                            <button
                                type="button"
                                className="av2-pop__option"
                                onClick={() => setActiveDim('city')}
                            >
                                <FormattedMessage id="analyticsV2.filter.city" defaultMessage="City" />
                            </button>
                            <button
                                type="button"
                                className="av2-pop__option"
                                onClick={() => setActiveDim('responsibility')}
                            >
                                <FormattedMessage
                                    id="analyticsV2.filter.responsibility"
                                    defaultMessage="Stage"
                                />
                            </button>
                        </div>,
                        portalTarget
                    )}

                    {pickerOpen && activeDim === 'client' && options && portalTarget && ReactDOM.createPortal(
                        <MultiSelectPopover
                            panelRef={panelRef}
                            style={popStyle}
                            items={options.clients.map((c) => ({
                                id: c.id,
                                label: c.name,
                                sub: c.city ?? '',
                            }))}
                            selected={value.clientIds}
                            onApply={(ids) => {
                                onChange({ ...value, clientIds: ids as number[] });
                                closePicker();
                            }}
                            onCancel={() => setActiveDim(null)}
                            placeholderId="analyticsV2.filter.searchClient"
                            placeholderDefault="Search clients…"
                        />,
                        portalTarget
                    )}

                    {pickerOpen && activeDim === 'city' && options && portalTarget && ReactDOM.createPortal(
                        <MultiSelectPopover
                            panelRef={panelRef}
                            style={popStyle}
                            items={options.cities.map((c) => ({ id: c, label: c }))}
                            selected={value.cities}
                            onApply={(ids) => {
                                onChange({ ...value, cities: ids as string[] });
                                closePicker();
                            }}
                            onCancel={() => setActiveDim(null)}
                            placeholderId="analyticsV2.filter.searchCity"
                            placeholderDefault="Search cities…"
                        />,
                        portalTarget
                    )}

                    {pickerOpen && activeDim === 'responsibility' && options && portalTarget && ReactDOM.createPortal(
                        <MultiSelectPopover
                            panelRef={panelRef}
                            style={popStyle}
                            items={options.responsibilities.map((r) => ({ id: r.id, label: r.name }))}
                            selected={value.responsibilityIds}
                            onApply={(ids) => {
                                onChange({ ...value, responsibilityIds: ids as number[] });
                                closePicker();
                            }}
                            onCancel={() => setActiveDim(null)}
                            placeholderId="analyticsV2.filter.searchResp"
                            placeholderDefault="Search stages…"
                        />,
                        portalTarget
                    )}
                </div>

                {hasAny && (
                    <button type="button" className="av2-filters__clear" onClick={clearAll}>
                        <FormattedMessage id="analyticsV2.filter.clearAll" defaultMessage="Clear all" />
                    </button>
                )}
            </div>
        </div>
    );
};

// ── Multi-select popover ─────────────────────────────────────────────────────

interface MSItem {
    id: number | string;
    label: string;
    sub?: string;
}

interface MSProps {
    items: MSItem[];
    selected: (number | string)[];
    onApply: (ids: (number | string)[]) => void;
    onCancel: () => void;
    placeholderId: string;
    placeholderDefault: string;
    panelRef?: React.Ref<HTMLDivElement>;
    style?: React.CSSProperties;
}

const MultiSelectPopover: React.FC<MSProps> = ({
    items,
    selected,
    onApply,
    onCancel,
    placeholderId,
    placeholderDefault,
    panelRef,
    style,
}) => {
    const intl = useIntl();
    const [draft, setDraft] = useState<(number | string)[]>(selected);
    const [search, setSearch] = useState('');

    const filtered = items.filter((it) => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return it.label.toLowerCase().includes(q) || (it.sub ?? '').toLowerCase().includes(q);
    });

    const toggle = (id: number | string) => {
        setDraft((d) => (d.includes(id) ? d.filter((x) => x !== id) : [...d, id]));
    };

    return (
        <div className="av2-pop av2-pop--multi" role="dialog" ref={panelRef} style={style}>
            <input
                type="text"
                className="av2-pop__search"
                placeholder={intl.formatMessage({ id: placeholderId, defaultMessage: placeholderDefault })}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
            />
            <div className="av2-pop__list">
                {filtered.length === 0 ? (
                    <div className="av2-pop__empty">
                        <FormattedMessage
                            id="analyticsV2.filter.noResults"
                            defaultMessage="No matches"
                        />
                    </div>
                ) : (
                    filtered.slice(0, 200).map((it) => {
                        const checked = draft.includes(it.id);
                        return (
                            <label key={String(it.id)} className="av2-pop__item">
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggle(it.id)}
                                />
                                <span className="av2-pop__item-text">
                                    <span className="av2-pop__item-label">{it.label}</span>
                                    {it.sub && <span className="av2-pop__item-sub">{it.sub}</span>}
                                </span>
                            </label>
                        );
                    })
                )}
            </div>
            <div className="av2-pop__footer">
                <button type="button" className="av2-pop__btn av2-pop__btn--ghost" onClick={onCancel}>
                    <FormattedMessage id="analyticsV2.filter.cancel" defaultMessage="Cancel" />
                </button>
                <button
                    type="button"
                    className="av2-pop__btn av2-pop__btn--primary"
                    onClick={() => onApply(draft)}
                >
                    <FormattedMessage id="analyticsV2.filter.apply" defaultMessage="Apply" />
                </button>
            </div>
        </div>
    );
};

export default FilterBar;
